// @ts-check
'use strict';

/**
 * Rating scales. Each contract rates condition on its own scale (West Moreton
 * HHS uses 1–5; G8 Education uses Poor / Fair / Good), but roll-ups, closing
 * sentences and defaults all work on three bands — poor, fair, good — so a
 * rating is stored as its value plus the scale it came from and mapped to a
 * band here. Trends then survive a contract changing scale.
 */

/** @typedef {'poor' | 'fair' | 'good'} Band */

/** Worst first. Higher rank = worse condition. */
const BAND_RANK = Object.freeze({ poor: 3, fair: 2, good: 1 });

/** @type {Readonly<Record<Band, string>>} */
const BAND_LABEL = Object.freeze({ poor: 'Poor', fair: 'Fair', good: 'Good' });

/**
 * @typedef {object} ScaleLevel
 * @property {string | number} value  what is stored
 * @property {string} label           what the assessor and the report show
 * @property {Band} band
 * @property {string} [description]   the contract's own wording, where it has one
 */

/**
 * @typedef {object} Scale
 * @property {string} key
 * @property {string} label
 * @property {ScaleLevel[]} levels    worst first
 */

/** @type {Readonly<Record<string, Scale>>} */
const SCALES = Object.freeze({
  'poor-fair-good': {
    key: 'poor-fair-good',
    label: 'Poor / Fair / Good',
    levels: [
      { value: 'poor', label: 'Poor', band: 'poor' },
      { value: 'fair', label: 'Fair', band: 'fair' },
      { value: 'good', label: 'Good', band: 'good' },
    ],
  },
  // Region Group's condition index. PLACEHOLDER descriptions: Region's wording for each rating is
  // still owed (Asana "Region Group's wording for each 1–3 condition rating"); replace, keep values.
  'one-to-three': {
    key: 'one-to-three',
    label: '1–3 (Poor to Good)',
    levels: [
      { value: 1, label: 'Poor', band: 'poor', description: '[Region wording to come] Poor' },
      { value: 2, label: 'Fair', band: 'fair', description: '[Region wording to come] Fair' },
      { value: 3, label: 'Good', band: 'good', description: '[Region wording to come] Good' },
    ],
  },
  // QLD Health SOA condition rating (WMHHS Contractor Scope §2.3).
  'one-to-five': {
    key: 'one-to-five',
    label: '1–5 (Very Poor to Excellent)',
    levels: [
      { value: 1, label: 'Very Poor', band: 'poor', description: 'Asset failed; not operational' },
      { value: 2, label: 'Poor', band: 'poor', description: 'Badly deteriorated; significant major defects' },
      { value: 3, label: 'Fair', band: 'fair', description: 'Average condition; attention and outstanding maintenance required' },
      { value: 4, label: 'Good', band: 'good', description: 'Superficial wear and tear; minor defects' },
      { value: 5, label: 'Excellent', band: 'good', description: 'No defects; appearance as new' },
    ],
  },
});

/**
 * The level a stored rating means on a scale, or null for no rating or an
 * unknown value. Compares as text, so a 3 read back from JSON as "3" matches.
 *
 * @param {string} scaleKey
 * @param {string | number | null | undefined} value
 * @returns {ScaleLevel | null}
 */
function levelOf(scaleKey, value) {
  if (value === null || value === undefined || value === '') return null;
  const scale = SCALES[scaleKey];
  if (!scale) return null;
  return scale.levels.find((l) => String(l.value) === String(value)) || null;
}

/**
 * @param {string} scaleKey
 * @param {string | number | null | undefined} value
 * @returns {Band | null}
 */
function bandOf(scaleKey, value) {
  const level = levelOf(scaleKey, value);
  return level ? level.band : null;
}

/**
 * The worse of two bands; either may be null.
 *
 * @param {Band | null} a
 * @param {Band | null} b
 * @returns {Band | null}
 */
function worseBand(a, b) {
  if (!a) return b;
  if (!b) return a;
  return BAND_RANK[a] >= BAND_RANK[b] ? a : b;
}

/**
 * How a stored rating reads on the report: "2 – Poor" on a numbered scale,
 * "Poor" on a named one; '' when there's no rating.
 *
 * @param {string} scaleKey
 * @param {string | number | null | undefined} value
 */
function ratingLabel(scaleKey, value) {
  const level = levelOf(scaleKey, value);
  if (!level) return '';
  return typeof level.value === 'number' ? `${level.value} – ${level.label}` : level.label;
}

/**
 * The scale a label names. A scale's label is the one stored in CRM
 * (hig_ratingscale, measure-to-quote's condition-schema.json), so the app and
 * its API both read a visit's scale through here. Null for an unknown label.
 *
 * @param {unknown} label
 * @returns {Scale | null}
 */
function scaleByLabel(label) {
  return Object.values(SCALES).find((s) => s.label === label) || null;
}

module.exports = { BAND_RANK, BAND_LABEL, SCALES, levelOf, bandOf, ratingLabel, worseBand, scaleByLabel };
